import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import SubmitButton from '../input/SubmitButton';
import './Form.css';
import { motion } from 'framer-motion';
import Popup from '../popup/Popup';
import { usePopup } from '../context/PopupContext';
import { useOffline } from '../context/OfflineContext';
import Icon from '../home/Icon';

function CreateForm({children, onSubmit, icon = 'person_add', error = null, tooltip = ''}) {
  const {openPopup} = usePopup();
  // Anlegen braucht immer das Netz -> im Offline-Modus zentral gesperrt (für alle Seiten mit CreateForm).
  const { isOffline } = useOffline();

  // Width-based mobile/desktop switch (1000px), kept live so rotating an iPad
  // between portrait and landscape swaps the form between slide-up and inline.
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 1000);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1000);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleSubmit = (event) => {
    if (isOffline) {
      event.preventDefault();
      return;
    }
    if (onSubmit) {
      onSubmit(event);
    }
    //handleFormControlsClick();
  };

  useEffect(() => {
    if (isMobile && error !== null) {
      openPopup("createerror");
    }
  }, [error, openPopup, isMobile])

  const [formVariants, setFormVariants] = useState({hidden: {top: window.innerWidth < 1000 ? "100dvh" : null}});
  const [isFormVisible, setFormVisible] = useState(false);
  const [isNearBottom, setIsNearBottom] = useState(false);
  const formRef = useRef();
  // Mobile: aktuelle Höhe des Sheets. Felder können nach dem Mount noch auftauchen/verschwinden
  // (z. B. Zusatz-Eingaben in Event.js erst nach dem Laden der Spieltag-Daten, Offline-Hinweis) —
  // ohne Nachmessen stimmt der Slide-up-Versatz nicht mehr (Lücke bzw. Button hinter der Bottom-Nav).
  const [formHeight, setFormHeight] = useState(0);

  useEffect(() => {
    const el = formRef.current;
    if (!isMobile || !el || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => setFormHeight(el.offsetHeight));
    observer.observe(el);
    return () => observer.disconnect();
  }, [isMobile]);

  useEffect(() => {
    if (!isMobile) return;
    const threshold = 80;
    const handleScroll = () => {
      const isScrollable = document.documentElement.scrollHeight > window.innerHeight + threshold;
      const nearBottom = isScrollable && window.scrollY + window.innerHeight >= document.documentElement.scrollHeight - threshold;
      setIsNearBottom(nearBottom);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isMobile]);

  const iconContainerVariants = {
    hidden: {
      x: "-50%",
      y: "-150px"
    },
    visible: {
      x: "-50%",
      y: "-50px"
    }
  }

  const iconVariants = {
    hidden: {
      rotate: 90,
      transition: {
        stiffness: 200
      }
    },
    visible: {
      rotate: 270,
      transition: {
        stiffness: 200
      }
    }
  };

  const overlayVariants = {
    hidden: {
      opacity: 0,
      transitionEnd: {
        display: "none"
      },
    },
    visible: {
      opacity: 0.5,
      display: "block"
    }
  };

  const handleFormControlsClick = () => {
    if (isMobile) {
      setFormVisible((prev) => !prev);
    }
  };

  // Recompute the variants for the current mode. Mobile gets the slide-up
  // geometry; desktop drops the offset so the inline grid form isn't pushed
  // down by a leftover motion `top`. Runs on every mobile/desktop switch
  // (window resize, iPad rotation) and, on mobile, whenever the sheet's
  // height changes (formHeight from the ResizeObserver above).
  useLayoutEffect(() => {
    if (isMobile) {
      const offset = formRef.current ? formRef.current.offsetHeight : 0;
      setFormVariants({
        hidden: { top: "100dvh" },
        visible: {
          top: `calc(100dvh - var(--bottom-nav-height) - env(safe-area-inset-bottom, 0px) - 8px - ${offset}px)`
        }
      });
    } else {
      // Desktop: a concrete `top: 0` (not null) so framer actually clears any
      // leftover slide-up offset — it can't animate back to `auto`/null and
      // would otherwise keep the form pushed down after a mobile→desktop resize.
      setFormVariants({ hidden: { top: 0 } });
    }
  }, [formRef, isMobile, formHeight]);

  // Collapse the form whenever the mode switches (resize across 1000px), so it
  // never gets stranded mid-page. The declarative animate below reacts to both
  // this state change and the recomputed variants above.
  useEffect(() => {
    setFormVisible(false);
  }, [isMobile]);

  const animateState = isFormVisible ? "visible" : "hidden";

  return (
    <React.Fragment>
      <motion.div
        className={"formOverlay"}
        variants={overlayVariants}
        initial="hidden"
        animate={animateState}
      >
      </motion.div>
      <motion.aside
        ref={formRef}
        className={"formContainer"}
        variants={formVariants}
        initial="hidden"
        animate={animateState}
      >
        <motion.div
          onClick={handleFormControlsClick}
          className={`formControls${isNearBottom && !isFormVisible ? ' formControls--nearBottom' : ''}`}
          variants={iconContainerVariants}
          data-tour="create-form-toggle"
        >
          <motion.span
            className={"material-symbols-rounded formControlsIcon"}
            variants={iconVariants}
          >
            arrow_back_ios_new
          </motion.span>
        </motion.div>
        <form onSubmit={handleSubmit}>
          {children}
          {onSubmit && isOffline && (
            <p className="formOfflineHint">
              <Icon className="formOfflineHintIcon" icon="cloud_off" />
              Offline nicht verfügbar
            </p>
          )}
          {onSubmit && <SubmitButton icon={icon} tooltip={tooltip} label={tooltip} disabled={isOffline} />}
          {error !== null && !isMobile && <p className={"newError"}>{error}</p>}
        </form>
      </motion.aside>
      <Popup name='createerror' confirmText='Ok' headerText='Error' type="error">
        <p>{error}</p>
      </Popup>
    </React.Fragment>
  )
}

export default CreateForm